import React, { useRef } from 'react';
import { GridView } from '@vivli/shared/views';
import { ColDef, GridApi, ICellRendererParams, RowNode } from 'ag-grid-community';
import { IDataRequestActionDetailDto, IDisclosureActionDetailDto } from '@vivli/features/dashboard/infrastructure/interface';
import { DataRequestActionCardView } from '@vivli/features/dashboard/views';
import { actionGridContainerStyle } from '../awaiting-actions/actions-grids-styles';

interface DataRequestActionsGridComponentProps {
    dtos?: IDataRequestActionDetailDto[];
    onGridReady?: (gridApi: GridApi) => void;
    emptyMessage?: string;
    hideFloatingFilter?: boolean;
    hideHeader?: boolean;
    autoHeight?: boolean;
    asyncUpdateKey?: string;
    isVivliAdmin?: boolean;
    studiesLabel?: string;
}

export const DataRequestActionsGridComponent = ({
    dtos,
    onGridReady,
    isVivliAdmin,
    studiesLabel
}: DataRequestActionsGridComponentProps) => {
    const gridApiRef = useRef<GridApi>();

    const handleOnGridReady = (gridApi: GridApi) => {
        onGridReady && onGridReady(gridApi);
        gridApiRef.current = gridApi;
    };

    //hidden columns for data used in full width renderer
    //that need to be used in export only
    const columnDefs: ColDef[] = [
        {
            field: 'vivliId',
            headerName: 'Vivli ID',
            filter: true,
            sortable: true,
            maxWidth: 130
        },
        {
            field: 'title',
            headerName: 'Request Title',
            filter: true,
            sortable: true
        },
        {
            field: 'leadInvestigatorFullName',
            headerName: 'Lead Investigator',
            filter: true,
            sortable: true,
            valueGetter: ({ data }) => `${data?.leadInvestigatorFirstName} ${data?.leadInvestigatorLastName}`
        },
        {
            field: 'daysLeft',
            headerName: 'Days in Current Step',
            filter: true,
            sortable: true,
            maxWidth: 140
        },
        {
            field: 'numberOfMyStudies',
            headerName: 'Number of My Studies',
            filter: true,
            sortable: true,
            hide: isVivliAdmin ? true : false,
            maxWidth: 140
        },
        {
            field: 'numberOfStudies',
            headerName: 'Total Number of Studies',
            filter: true,
            sortable: true,
            hide: isVivliAdmin ? false : true,
            maxWidth: 140
        },
        {
            field: 'requestAdminFullName',
            headerName: 'Requested By',
            filter: true,
            sortable: true,
            valueGetter: ({ data }) => `${data?.requestAdminFirstName} ${data?.requestAdminLastName}`
        },
        {
            field: 'actionText',
            headerName: 'Action Required',
            filter: true,
            sortable: true,
            maxWidth: 250
        },
        {
            field: 'daysWarning',
            headerName: 'Action Overdue',
            filter: true,
            sortable: true,
            hide: true,
            valueGetter: ({ data }) => (data.daysWarning ? 'Yes' : 'No')
        },
        {
            field: 'contributors',
            headerName: 'Data Contributors',
            filter: false,
            sortable: false,
            hide: true,
            valueGetter: ({ data }) => (data?.contributors ?? []).join(', ')
        },
        {
            field: 'leadInvestigatorAffiliation',
            headerName: 'Lead Investigator Affiliation',
            filter: false,
            sortable: false,
            hide: true
        },
        {
            field: 'leadInvestigatorCountry',
            headerName: 'Lead Investigator Country',
            filter: false,
            sortable: false,
            hide: true
        },
        {
            field: 'currentWorkflowStepTargetDays',
            headerName: 'Target Days for Current Workflow Step',
            filter: false,
            sortable: false,
            hide: true
        }
    ];

    const getFullWidthRenderer = ({ data }: ICellRendererParams<IDataRequestActionDetailDto>) => (
        <DataRequestActionCardView dto={data} isVivliAdmin={isVivliAdmin} studiesLabel={studiesLabel}></DataRequestActionCardView>
    );

    return (
        <div style={actionGridContainerStyle}>
            <GridView
                rowData={dtos}
                useFullWidthRows={true}
                onGridReady={handleOnGridReady}
                fullWidthCellRenderer={getFullWidthRenderer}
                columns={columnDefs}
                rowHeight={130}
                emptyMessage={'No Data Request Actions found, please click Data Requests tab to refresh'}
            />
        </div>
    );
};
